import React, { useEffect } from 'react';
import { PropTypes } from 'prop-types';
import Table from '../../common/components/table/agGrid/Table';
import { SearchRow } from './suggestedAgents/SearchRow';
import { getCurrentTabTimePeriod, getTabFilters, getTabDescription, getHasResults } from './suggestedAgents/helpers/helpers';
import { useSearchStore, useCommonStore } from '../../store/store';
import { views } from '../constants/savedAgents';
import { getSummaryPinnedConfig } from '../../common/components/table/agGrid/pinnedRowSettings';
import { tabs, terms } from '../constants/suggestedAgents';
import { useFloatingActionBar } from '../hooks/floatingActionBar';
import AgentsFloatingActionBar from './common/AgentsFloatingActionBar';
import { exportCsv } from './common/exportCsv';

export const SuggestedAgentsResults = props => {
    const { tableSettings, gridRef, rowData = [], setState, state } = props;
    const { 
        timeIntervals,
        } = useCommonStore(state => ({
        timeIntervals: state.timeIntervals
    }));
    const {
        currentInactivityPeriod,
        currentTabId,
        currentTabIndex,
        currentTabTimePeriod,
        currentTabProducers,
        highProducingAgents,
        selectedListingViewId,
        summaryPinnedData,
        tabDescription,
        setTabDescription,
        minGrowth,
        maxGrowth,
        growthAnalysisPeriod,
    } = useSearchStore(state => {
        const moduleState = state[state.selectedModule];
        const { currentTabId, searchOptions } = moduleState;
        const currentTabState = searchOptions?.[currentTabId];
        const { display, producers } = currentTabState;
        const selectedListingViewId = display.selectedView.value;
        const isGrowthAnalysisView = selectedListingViewId === views.growthAnalysis;
        const currentTabTimePeriod = getCurrentTabTimePeriod(producers, isGrowthAnalysisView);
        const currentInactivityPeriod = producers?.inactivity?.label || null;
        const highProducingAgents = state.selectedFilterValues?.[currentTabId]?.lowPos?.label || producers?.lowPos?.label;
        const isIncDecProducers = currentTabId === tabs.incProducers.id || currentTabId === tabs.decProducers.id;
        return ({
            currentInactivityPeriod,
            currentTabId,
            currentTabIndex: moduleState?.currentTabIndex,
            currentTabTimePeriod,
            currentTabProducers: producers,
            selectedListingViewId,
            summaryPinnedData: moduleState?.summaryPinnedData,
            highProducingAgents,
            tabDescription: currentTabState?.tabDescription || '',
            setTabDescription: state.setTabDescription,
            minGrowth: producers?.minGrowth,
            maxGrowth: producers?.maxGrowth,
            growthAnalysisPeriod: isIncDecProducers
                ? producers?.growthTimeFrame?.value
                : currentTabState?.growthAnalysisPeriod?.value,
        });
    });

    const handleDownload = (selectedAgents) => {
        exportCsv(
            {
                ...state,
                listingProfColumns: state.listingProficiencyColumns,
                timeIntervals,
                growthAnalysisPeriod,
            },
            setState,
            selectedListingViewId,
            selectedAgents,
            currentTabId
        );
    };

    const {
        selectedCount,
        showAddToListModal,
        setShowAddToListModal,
        handleDownloadClick,
        handleDismiss,
    } = useFloatingActionBar(gridRef, handleDownload);

    const revisedRowDataByTab = getTabFilters(rowData, currentTabProducers)?.[currentTabId] ?? rowData;
    const shouldShowPinnedRow = [views.overview, views.listingProficiency].includes(selectedListingViewId);
    const summaryPinnedConfig = getSummaryPinnedConfig(selectedListingViewId);
    const hasResults = getHasResults(state?.rowDataByTab[currentTabId]);
    const tabTitle = Object.values(tabs).map(tab => tab.label)[currentTabIndex];

    useEffect(() => {
        const tabDescription = getTabDescription(
            currentTabId,
            {
                inactivityPeriod: currentInactivityPeriod,
                timePeriod: currentTabTimePeriod?.label,
                highProducingAgents,
                minGrowth: minGrowth,
                maxGrowth: maxGrowth,
            }
        );
        setTabDescription(tabDescription);
    }, []);

    return (<div className="pt-3">
        <SearchRow
            {...(hasResults && { tabDescription })}
            tabTitle={tabTitle}
            tabKey={currentTabId}
            setState={setState}
            state={state}
            tableSettings={tableSettings}
            selectedListingViewId={selectedListingViewId}
            growthAnalysisPeriod={growthAnalysisPeriod}
        />
        {hasResults ? <div className='suggested-agents-table'>
            <Table
                key={tableSettings?.tableView}
                additionalTableHeaders={tableSettings?.additionalTableHeaders}
                columns={tableSettings?.columns}
                externalParams={shouldShowPinnedRow && { summaryPinnedConfig }}
                gridRef={gridRef}
                pinnedBottomRowData={shouldShowPinnedRow ? [{ ...summaryPinnedData }] : []}
                rowData={revisedRowDataByTab}
                tableTitleWithTooltips={tableSettings?.tooltips}
                tableView={tableSettings?.tableView}
                rowSelection={'multiple'}
                suppressRowClickSelection={true}
                rowMultiSelectWithClick={true}
            />
            <AgentsFloatingActionBar
                selectedCount={selectedCount}
                showAddToListModal={showAddToListModal}
                setShowAddToListModal={setShowAddToListModal}
                onDismiss={handleDismiss}
                onDownloadClick={handleDownloadClick}
            />
        </div>
            : <div className="sugg-agents-no-results d-flex justify-content-center align-items-center">
                <div className='text-center'>
                    <h5>{terms.noTabResultsFound(tabTitle)}</h5>
                    <p className="font-gray-primary">{terms.adjustFilters}</p>
                </div>
            </div>}
    </div>);
};

SuggestedAgentsResults.propTypes = {
    tableSettings: PropTypes.object,
    gridRef: PropTypes.object,
    rowData: PropTypes.array,
    setState: PropTypes.func,
    state: PropTypes.object,
};
